<script>
import { mapGetters, mapState, mapActions } from 'vuex';
import store from '../../../store';
import i18n from '../../../i18n';
import TwTitleBar from '../../../components/TwTitleBar';

export default {
  name: 'SkiRentalSeason',
  components: {
    TwTitleBar,
  },
  props: {
    seasonId: {
      type: [ Number, String ],
      default: null,
    },
    new: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentSeasonId: this.seasonId,
      activeTab: `/ski/rental/season/${this.currentSeasonId}/price-settings`,
      tabs: [
        {
          id: 1,
          name: i18n.t('skiRental.tabHeaders.priceSettings'),
          route: '/ski/rental/season/:seasonId/price-settings',
          total: 0,
          showTotal: false,
          disabled: false,
        },
        {
          id: 2,
          name: i18n.t('skiRental.tabHeaders.personCategories'),
          route: '/ski/rental/season/:seasonId/person-categories',
          total: 0,
          showTotal: true,
          disabled: true,
        },
        {
          id: 3,
          name: i18n.t('skiRental.tabHeaders.prices'),
          route: '/ski/rental/season/:seasonId/prices',
          total: 0,
          showTotal: true,
          disabled: true,
        },
        {
          id: 4,
          name: i18n.t('skiRental.tabHeaders.discounts'),
          route: '/ski/rental/season/:seasonId/discounts',
          total: 0,
          showTotal: true,
          disabled: true,
        },
        {
          id: 5,
          name: i18n.t('skiRental.tabHeaders.areas'),
          route: '/ski/rental/season/:seasonId/areas',
          total: 0,
          showTotal: true,
          disabled: true,
        },
        {
          id: 6,
          name: 'Pickup places',
          route: '/ski/rental/season/:seasonId/pickup-places',
          total: 0,
          showTotal: false,
          disabled: true,
        },
      ],
    };
  },
  computed: {
    ...mapState({
      season: state => state.skirental.season,
      domains: state => state.twenty.domains.items,
    }),
    ...mapGetters({
      season: 'skirental/getSeasonWithRelations',
    }),
    breadcrumbs() {
      return [
        { text: this.$t('menu.ski.title'), to: '', disabled: true },
        { text: this.$t('breadcrumbs.SkiRentalSeasons'), to: { name: 'SkiRentalSeasons' } },
        { text: this.season.id ? this.season.name : 'New', to: '', disabled: true },
        { text: this.$t(`breadcrumbs.${this.$route.name}`), to: this.$route.path },
      ];
    },
    updatedSeasonMenu() {
      const tabs = this.tabs;
      tabs[0].name = this.isNewSeasonInProgress
        ? i18n.t('ski.skiPriceSettings.newSeason')
        : i18n.t('ski.skiPriceSettings.title');
      tabs[0].route = this.isNewSeasonInProgress
        ? '/ski/rental/season/new/price-settings'
        : '/ski/rental/season/:seasonId/price-settings';
      if (!this.currentSeasonId) {
        return tabs;
      }
      const selectedSeason = this.season;
      if (selectedSeason) {
        for (let i = 0; i < tabs.length; i += 1) {
          tabs[i].disabled = false;
          tabs[i].route = tabs[i].route.replace(':seasonId', this.currentSeasonId);
          switch (tabs[i].id) {
            case 1:
              tabs[i].total = selectedSeason.name.length
                && selectedSeason.netOrGross.length
                && selectedSeason.twentySupplierId
                && selectedSeason.numPeriodDates
                ? 1
                : 0;
              break;
            case 2:
              tabs[i].total = selectedSeason.numPersonCategories;
              break;
            case 3:
              tabs[i].total = selectedSeason.numPrices;
              break;
            case 4:
              tabs[i].total = selectedSeason.numDiscounts;
              break;
            case 5:
              tabs[i].total = selectedSeason.numAreas;
              break;
            default:
              break;
          }
        }
      }

      return tabs;
    },
    isNewSeasonInProgress() {
      return this.currentSeasonId === null;
    },
  },
  methods: {
    ...mapActions({
      loadSeasonWithRelations: 'skirental/seasonWithRelationsBySeasonIdLoad',
      loadXmlChannels: 'twenty/xmlChannels',
      loadXmlProducts: 'twenty/xmlProducts',
      getDomains: 'twenty/getDomains',
    }),
    newSeasonCreated(newSeason) {
      this.currentSeasonId = newSeason.seasonId;
      this.loadSeasonWithRelations(newSeason.seasonId);
    },
  },
  mounted() {
    this.loadXmlChannels(3);
    this.loadXmlProducts(3);
    this.loadXmlProducts(6);
    if (!this.season && this.seasonId) {
      this.loadSeasonWithRelations(this.seasonId);
    }
    if (!this.domains.length) {
      this.getDomains();
    }
  },
  beforeRouteEnter(to, from, next) {
    if (!Object.keys(to.params).length || to.params.new !== undefined) {
      store.dispatch('skirental/seasonByIdLoad', undefined)
        .finally(() => {
          next();
        });
    } else if (to.params.seasonId) {
      store.dispatch('skirental/seasonWithRelationsBySeasonIdLoad', to.params.seasonId)
        .finally(() => {
          next();
        });
    }
  },
};
</script>

<template>
  <v-container fluid>
    <tw-title-bar
      :title="$t('menu.ski.pages.skiRental.title')"
      :breadcrumbs="breadcrumbs"
      :domains="domains"
      should-show-domain-selector
    />
    <v-layout class="pt-2" row wrap>
      <v-flex class="pr-12" grow>
        <v-card class="ski-area__card">
          <v-tabs
            v-model="activeTab"
            class="ski-area__tabs"
            grow
            fixed-tabs
          >
            <v-tab
              v-for="tab in updatedSeasonMenu"
              v-bind="tab"
              :key="tab.id"
              :to="tab.route"
              :disabled="tab.disabled"
              exact
            >
              <v-icon
                :color="tab.total ? 'green' : 'red'"
                :disabled="tab.disabled"
                class="ml-2"
                small
              >
                {{ tab.total ? 'check_circle' : 'error' }}
              </v-icon>
              {{ tab.name }}
              <span v-if="tab.showTotal">
                &nbsp;({{ tab.total }})
              </span>
            </v-tab>
            <v-tabs-items>
              <v-tab-item
                v-for="tab of tabs"
                :transition="false"
                :reverse-transition="false"
                :key="tab.id"
                :value="tab.route"
              />
            </v-tabs-items>
          </v-tabs>
          <div class="pa-5 pl-4" grid-list-xs>
            <router-view @newSeasonCreated="newSeasonCreated"/>
          </div>
        </v-card>
      </v-flex>
    </v-layout>
  </v-container>
</template>



// WEBPACK FOOTER //
// src/pages/ski/rental/SkiRentalSeason.vue